import React, { useState } from 'react';
import { I18n } from 'react-redux-i18n';
import { Button, Tooltip, Row } from 'antd';
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  SettingOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import { Moment } from 'moment';

import Card from 'components/Card';
import DatePicker from 'components/DatePicker';
import Icon from 'components/Icon';
import Select from 'components/Select/SelectFilter';
import GlobalTagsModal from 'modules/Media/components/TagsModal/GlobalTagsModal';

import {
  ArticleFilter,
  ArticleFilterAPI,
  formatArticleFilterAPI,
  useArticleHeader,
  useArticlesRemoveTagsMutation
} from 'apiTyped/hooks/articles';
import { useDisclose } from 'utils/hooks/useDisclosure';

const isMobile = window.innerWidth <= 845;

const Search = styled.div`
  background-color: ${(props) => props.theme.colors['white']};
  width: 100%;

  @media (max-width: ${(props) =>
      props.theme.devices.isTablet ? '0px' : '845px'}) {
    margin: 0;
  }

  @media (max-width: 1200px) {
    display: grid;
    gap: 16px;
    margin: 0;
  }

  .searchLabel {
    text-transform: uppercase;
    font-size: 12px;
    font-family: ${(props) => props.theme.fonts.oswald};
    color: #2b3d54;
  }

  .searchContainer {
    color: #1f2b37;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    font-size: 12px;
    font-weight: 400;
    position: relative;
    width: 100%;
  }

  .searchContent {
    display: flex;
    gap: 20px;
    align-items: center;
    justify-content: space-between;
    padding: 0 10px;
    border-radius: 2px;
    margin-top: 7px;
    background-color: rgba(39, 53, 68, 0.03);
  }

  .searchInput {
    background: none;
    box-sizing: border-box;
    height: 45px;
    border: 0;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    transition: all 100ms ease 0s;
    position: relative;
    width: 100%;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
  }
`;

const MediaFiltersContainer = styled.div`
  .row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;

    @media (max-width: ${(props) =>
        props.theme.devices.isTablet ? '0px' : '845px'}) {
      grid-template-columns: 1fr;
      align-items: center;
      gap: 16px;
    }
  }

  .ant-btn.ant-btn-circle.ant-btn.button-parameter {
    width: unset;
    min-width: unset;
    height: unset;
    padding: 0;
  }

  .expandIconContainer {
    display: flex;
    gap: 5px;
    justify-content: center;
    font-family: ${(props) => props.theme.fonts.oswald};
    color: rgb(134, 142, 150);
  }
`;

const dateStyle = `
  .SingleDatePickerInput{
    border: 0;
    border-bottom: 1px solid rgba(134,142,150,0.3);
    height: 25px;
  }
  .DateInput{
    height: 25px;
    margin-left: 0px;
  }
  .DateInput_input{
    height: 25px;
  }
  .SingleDatePicker_picker{
    margin-left: 40px;
    top: 35px !important;
  }

  .DateInput_fangShape{
    fill: #fbfcfc;
  }

  .DateInput_fang{
    top: 25px !important;
    left: 85px !important;
  }
`;

type MediaFilterProps = {
  filterValue: ArticleFilter;
  // eslint-disable-next-line no-unused-vars
  onFilterChange: (filter: ArticleFilter) => void;
};

const MediaFilters = ({ filterValue, onFilterChange }: MediaFilterProps) => {
  const { articleHeader } = useArticleHeader();

  const filters: ArticleFilterAPI = formatArticleFilterAPI(filterValue);
  const tagsMutation = useArticlesRemoveTagsMutation(filters);
  const globalTagsModal = useDisclose();

  const [loading, setLoading] = React.useState(false);
  const showFiltersMobile = useDisclose();

  const categoriesOptions = articleHeader?.categories.map((category) => ({
    value: category.id,
    label: category.name,
    is_active: category.is_active
  }));

  const availableTags =
    articleHeader?.tags.map((tag) => ({
      value: tag.id,
      label: tag.name
    })) || [];

  const tagsOptions = [
    { label: I18n.t('media.optionTags.isFavorite'), value: 'is_favorite' },
    { label: I18n.t('media.optionTags.isFeatured'), value: 'is_featured' },
    { label: I18n.t('media.optionTags.isPaper'), value: 'is_paper' },
    ...availableTags
  ];

  const submitTags = async (tags: number[]) => {
    setLoading(true);
    await tagsMutation.mutateAsync({ tags_to_remove: tags });
    setLoading(false);
  };

  const countriesOptions = articleHeader?.countries.map((country) => ({
    value: country.id,
    label: country.name
  }));

  const [fromFocus, setFromFocus] = useState(false);
  const [toFocus, setToFocus] = useState(false);

  const onSearchChange = (newFilterValue: ArticleFilter) =>
    onFilterChange(newFilterValue);

  const handleShowFiltersMobile = () => {
    if (showFiltersMobile.isOpen) showFiltersMobile.onClose();
    else showFiltersMobile.onOpen();
  };

  const SelectCategory = () => (
    <Select
      dropdownIcon={null}
      isDisabled={false}
      selectedOption={filterValue.category}
      options={categoriesOptions}
      onChange={(category?: { label: string; value: number }) =>
        onFilterChange({ ...filterValue, category })
      }
      label={I18n.t('media.filters.category')}
      placeholder={I18n.t('placeholderAll')}
      minWidth="100px"
    />
  );
  const SelectTags = () => (
    <Row justify="space-between" align="bottom">
      <Select
        dropdownIcon={null}
        isDisabled={false}
        selectedOption={filterValue.tags}
        options={tagsOptions}
        onChange={(tags: { label: string; value: string }[]) =>
          onFilterChange({ ...filterValue, tags })
        }
        label={I18n.t('media.filters.tags')}
        placeholder={I18n.t('placeholderAll')}
        isMulti
        style={{ width: 'calc(100% - 30px)' }}
      />
      <Tooltip title={I18n.t('media.globalTagsModal.title')}>
        <Button
          className="button-parameter"
          type="link"
          shape="circle"
          icon={<SettingOutlined />}
          onClick={globalTagsModal.onOpen}
        />
      </Tooltip>
    </Row>
  );
  const SelectCountry = () => (
    <Select
      dropdownIcon={null}
      isDisabled={false}
      selectedOption={filterValue.country}
      options={countriesOptions}
      onChange={(country?: { label: string; value: number }) =>
        onFilterChange({ ...filterValue, country })
      }
      label={I18n.t('media.filters.country')}
      placeholder={I18n.t('placeholderAll2')}
    />
  );
  const DatePickerStartDate = () => (
    <div className="calendarContainer">
      <DatePicker
        date={filterValue.fromDate}
        focused={fromFocus}
        onFocusChange={(focused: boolean) => {
          if (toFocus) setToFocus(false);
          if (fromFocus !== focused) setFromFocus(focused);
        }}
        onChange={(date: Moment) =>
          onFilterChange({ ...filterValue, fromDate: date })
        }
        id="from_date"
        label={I18n.t('media.filters.from')}
        styles={dateStyle}
      />
    </div>
  );
  const DatePickerEndDate = () => (
    <div className="calendarContainer">
      <DatePicker
        date={filterValue.toDate}
        focused={toFocus}
        onFocusChange={(focused: boolean) => {
          if (fromFocus) setFromFocus(false);
          if (toFocus !== focused) setToFocus(focused);
        }}
        onChange={(date: Moment) =>
          onFilterChange({ ...filterValue, toDate: date })
        }
        label={I18n.t('media.filters.to')}
        id="to_date"
        styles={dateStyle}
      />
    </div>
  );
  const SelectReadOrNot = () => (
    <Select
      dropdownIcon={null}
      isDisabled={false}
      selectedOption={
        filterValue.isRead == undefined
          ? null
          : filterValue.isRead
            ? { label: I18n.t('media.filters.read'), value: true }
            : { label: I18n.t('media.filters.notRead'), value: false }
      }
      options={[
        { label: I18n.t('media.filters.read'), value: true },
        { label: I18n.t('media.filters.notRead'), value: false }
      ]}
      onChange={(selected: { label: string; value: boolean }) =>
        onFilterChange({ ...filterValue, isRead: selected?.value })
      }
      label={I18n.t('media.filters.readLabel')}
      placeholder={I18n.t('placeholderAll')}
    />
  );

  const ExpandIcon = ({ active = true }: { active?: boolean }) => (
    <div
      className="expandIconContainer"
      onClick={() => handleShowFiltersMobile()}
    >
      {active ? (
        <>
          <EyeInvisibleOutlined style={{ fontSize: 16, color: '#868e96' }} />
          <p>{I18n.t('media.hideUserArticles')}</p>
        </>
      ) : (
        <>
          <EyeOutlined style={{ fontSize: 16, color: '#868e96' }} />
          <p>{I18n.t('media.showUserArticles')}</p>
        </>
      )}
    </div>
  );

  return (
    <MediaFiltersContainer>
      <Card>
        <div className="row">
          <label className="peer">
            <input
              id="showAlFiltersSelect"
              type="checkbox"
              checked={showFiltersMobile.isOpen}
            />
          </label>
          <SelectCategory />
          <SelectTags />
          <div className="hidden transition-all not-peer-has-checked peer-has-checked">
            <SelectCountry />
          </div>
          <div className="hidden transition-all not-peer-has-checked peer-has-checked">
            <DatePickerStartDate />
          </div>
          <div className="hidden transition-all not-peer-has-checked peer-has-checked">
            <DatePickerEndDate />
          </div>
          <div className="hidden transition-all not-peer-has-checked peer-has-checked">
            <SelectReadOrNot />
          </div>
        </div>
        <Search>
          <label className="peer">
            <input
              id="showAlFiltersSearch"
              type="checkbox"
              checked={showFiltersMobile.isOpen}
            />
          </label>
          <div className="searchContainer hidden transition-all not-peer-has-checked peer-has-checked">
            <p className="searchLabel">{I18n.t('media.searchLabel')}</p>
            <div className="searchContent">
              <input
                name="filterSearch"
                type="text"
                value={filterValue.search || ''}
                placeholder={I18n.t('media.searchPlaceholder')}
                onChange={(e) =>
                  onSearchChange({ ...filterValue, search: e.target.value })
                }
                className="searchInput"
              />
              <Icon
                alt="glass"
                className="magnifyingGlass"
                icon="magnifyingGlass"
              />
            </div>
          </div>
          {isMobile && <ExpandIcon active={showFiltersMobile.isOpen} />}
        </Search>
      </Card>
      <GlobalTagsModal
        open={globalTagsModal.isOpen}
        loading={loading}
        onClose={globalTagsModal.onClose}
        onSubmit={submitTags}
      />
    </MediaFiltersContainer>
  );
};

export { MediaFilters };
